/**
 * Configurações gerais da empresa. Venda para CNPJ: ligada por padrão; desligada, o pedido de pessoa jurídica não vai
 * para o ERP — no Mercado Livre a Berytus cancela sozinha (venda não concretizada, responsabilidade do comprador) e na
 * Shopee o pedido fica com alerta na tela de Pedidos para cancelar à mão.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Loading } from '../components/ui.tsx';
import { can } from '@berytus/contracts';

interface Settings { sellsToCnpj: boolean }

export function GeneralSettingsPage() {
  const me = useMe();
  const admin = can(me.tenant.access, 'settings', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['account-settings'], queryFn: () => api<Settings>('/account/settings') });
  const save = useMutation({
    mutationFn: (s: Settings) => api('/account/settings', { method: 'PUT', json: s }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['account-settings'] }),
  });
  const s = q.data;
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Configurações gerais</h1>
          <p>Regras da empresa que valem para todos os canais.</p>
        </div>
      </div>
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : s && (
        <section className="panel stack-y" style={{ gap: 12, maxWidth: 760 }}>
          <h2 style={{ margin: 0 }}>Pedidos</h2>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <input type="checkbox" className="check" style={{ marginTop: 3 }} checked={s.sellsToCnpj} disabled={!admin || save.isPending}
              onChange={(e) => {
                const on = e.target.checked;
                if (!on && !window.confirm('Deixar de vender para CNPJ? Pedidos novos de pessoa jurídica não vão para o ERP: no Mercado Livre a Berytus cancela sozinha; na Shopee aparece um alerta para cancelar à mão.')) return;
                save.mutate({ sellsToCnpj: on });
              }} />
            <span>
              <b>Vende para CNPJ (pessoa jurídica)</b>
              <div className="small muted" style={{ marginTop: 4 }}>
                Desmarcado: pedido novo com CNPJ do comprador não vai para o ERP. No Mercado Livre a Berytus cancela na hora (venda não
                concretizada por responsabilidade do comprador, com a mensagem "A loja não vende para CNPJ" e o estoque devolvido); pedidos
                do Full ou que o Mercado Livre não deixa cancelar, e os da Shopee, ficam com alerta na tela de Pedidos para cancelar à mão.
              </div>
            </span>
          </label>
          {save.error && <ErrorBox error={save.error} />}
          {!admin && <span className="small muted">Só o dono ou administradores alteram as configurações.</span>}
        </section>
      )}
    </>
  );
}
